/* eslint-disable max-lines */
import { AlarmGroupContainer } from '@hakimo-ui/hakimo/feature-shared';
import {
  CamAction,
  CurrentAndSiblingEscalations,
  UNIFIED_DATA_VERSION,
  UnifiedAlarmGroup,
  UnifiedMonitoringAlarmGroupStatusMessage,
  UnifiedMonitoringAlarmsMessage,
  UnifiedMonitoringMessageType,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { BlockerPrompt } from '@hakimo-ui/hakimo/ui-elements';
import { trackEvent, useUnifiedMonitoringWs } from '@hakimo-ui/hakimo/util';
import { useEffect, useRef, useState } from 'react';
import { getCurrentAndSiblingEscalations } from '../utils';
import { FETCH_ALARMS_DELTA_POLLING_INTERVAL } from './constants';
import { useUnifiedMonitoringWsMessageHandler } from './hooks/useUnifiedMonitoringWsMessageHandler';

interface Props {
  alarmGroup: UnifiedAlarmGroup;
  onAlarmGroupAction: (
    actionType: CamAction,
    alarmGroupId: string,
    comment?: string,
    cameraId?: string,
    cameraGroupId?: string,
    action?: string, // action and subaction is for resolve and escalate actions
    subAction?: string,
    reason?: string,
    category?: string
  ) => void;
  navigateToPendingAlarms: () => void;
}

export function SingleAlarmGroupView(props: Props) {
  const { alarmGroup, onAlarmGroupAction, navigateToPendingAlarms } = props;
  const [updatedAlarmGroup, setUpdatedAlarmGroup] = useState<UnifiedAlarmGroup>(
    () => alarmGroup
  );

  const [currentAndSiblingEscalations, setCurrentAndSiblingEscalations] =
    useState<CurrentAndSiblingEscalations>({});

  const alarmGroupRef = useRef<UnifiedAlarmGroup>(alarmGroup);

  const { send } = useUnifiedMonitoringWs();

  const sendMessageForDelta = () => {
    const alarmGroupData = alarmGroupRef.current;
    send?.({
      event_type: UnifiedMonitoringMessageType.UNIFIED_ALARMS,
      alarm_group_id: alarmGroupData.id,
      product: alarmGroupData.product,
      tenant_id: alarmGroupData.tenantId,
      existing_unified_alarm_ids: Array.from(
        alarmGroupData.uniqueAlarmIds ?? []
      ),
      alarm_timestamp_sec: new Date(alarmGroupData.timestamp).getTime() / 1000,
      unification_enabled: alarmGroupData.unificationEnabled,
      partition_key: alarmGroupData.unificationEnabled
        ? alarmGroupData.partition_key
        : undefined,
    });
  };

  useEffect(() => {
    sendMessageForDelta();
    const timer = window.setInterval(() => {
      sendMessageForDelta();
    }, FETCH_ALARMS_DELTA_POLLING_INTERVAL);
    return () => {
      timer && clearInterval(timer);
    };
  }, [send]);

  const handleUnifiedAlarmsResponse = (
    allData: UnifiedMonitoringAlarmsMessage[]
  ) => {
    allData.forEach((data) => {
      const {
        alarms: newAlarms,
        alarm_group_id: alarmGroupId,
        escalations,
      } = data.payload;
      const alarmGroupData = { ...alarmGroupRef.current };
      if (alarmGroupId !== alarmGroupData.id) {
        return;
      }
      const uniqueAlarmIds = alarmGroupData.uniqueAlarmIds ?? new Set<string>();

      newAlarms.forEach((alarm) => {
        if (!uniqueAlarmIds.has(alarm.id)) {
          uniqueAlarmIds.add(alarm.id);
          alarmGroupData.alarms = [...alarmGroupData.alarms, alarm];
          alarmGroupData.alarmCount = alarmGroupData.alarms.length;
        }
      });
      alarmGroupData.uniqueAlarmIds = uniqueAlarmIds;
      // This logic is to remove duplicate banners in case of loading alarm group from url
      // TODO: remove this and fix it with proper logic in AlarmGroupContainer
      if (alarmGroupData.product === UnifiedMonitoringProduct.SCAN) {
        alarmGroupData.escalations = [];
      } else {
        alarmGroupData.escalations = escalations;
      }
      setUpdatedAlarmGroup(alarmGroupData);
      alarmGroupRef.current = alarmGroupData;

      // update current and sibling escalation data
      const { currentAlarmEscalation, siblingEscalations } =
        getCurrentAndSiblingEscalations(escalations, {
          alarmGroupId,
          sourceGroupId: alarmGroupData.sourceGroupId,
          locationAlarmTypeId: alarmGroupData.locationAlarmTypeId,
        });

      setCurrentAndSiblingEscalations({
        currentAlarmEscalation,
        siblingEscalations,
      });
    });
  };

  useUnifiedMonitoringWsMessageHandler(
    UnifiedMonitoringMessageType.UNIFIED_ALARMS,
    handleUnifiedAlarmsResponse
  );

  const handleUnifiedAlarmGroupStatusResponse = (
    data: UnifiedMonitoringAlarmGroupStatusMessage
  ) => {
    const { alarm_group_id: alarmGroupId, status } = data.payload;
    const alarmGroupData = { ...alarmGroupRef.current };
    if (alarmGroupData.id !== alarmGroupId) {
      return;
    }
    if (alarmGroupData.status !== status) {
      alarmGroupData.status = status;
      alarmGroupRef.current = alarmGroupData;
      setUpdatedAlarmGroup(alarmGroupData);
    }
  };
  useUnifiedMonitoringWsMessageHandler(
    UnifiedMonitoringMessageType.UNIFIED_ALARM_GROUP_STATUS,
    handleUnifiedAlarmGroupStatusResponse
  );

  const handleAlarmGroupAction =
    (actionType: CamAction) =>
    (comment?: string, action?: string, subAction?: string) => {
      const firstAlarm = alarmGroup.alarms[0];
      const cameraGroupId = firstAlarm?.cameraGroupId ?? undefined;
      const isResolveEscalation = actionType === CamAction.RESOLVE_ESCALATION;
      onAlarmGroupAction(
        actionType,
        alarmGroup.id,
        comment,
        '',
        cameraGroupId,
        isResolveEscalation ? undefined : action,
        isResolveEscalation ? undefined : subAction,
        isResolveEscalation ? action : undefined,
        isResolveEscalation ? subAction : undefined
      );
      setTimeout(() => {
        sendMessageForDelta();
      }, 1000);
    };

  const handleClose = () => {
    const alarmGroupData = { ...alarmGroupRef.current };
    const wasResolved = alarmGroupData.status
      .toLowerCase()
      .includes('resolved');
    if (
      !wasResolved &&
      !window.confirm('If you leave now, this alarm will be dropped!')
    ) {
      return;
    }
    trackEvent('unified_monitoring_close_alarm', {
      alarmGroupId: alarmGroupData.id,
      product: alarmGroupData.product,
      wasResolved,
    });
    navigateToPendingAlarms();
  };

  // handle escalation data
  // 1. Allow resolving the current alarm
  // 2. Show sibling escalated alarms from the home location

  const isAlarmResolved = updatedAlarmGroup.status
    .toLowerCase()
    .includes('resolved');
  const blockerMessage = isAlarmResolved
    ? "You're about to leave Unified Monitoring. Are you sure you want to continue?"
    : 'If you leave now, this alarm will be dropped!';

  return (
    <BlockerPrompt shouldBlock={true} message={blockerMessage}>
      {/* <div className="fixed right-5 top-3 z-10 pr-3">
        <Button variant="outline" onClick={handleClose} className="flex gap-1">
          Close
        </Button>
      </div> */}
      <AlarmGroupContainer
        alarmGroup={updatedAlarmGroup}
        product={alarmGroup.product ?? UnifiedMonitoringProduct.REMOTE_GUARDING}
        dataVersion={
          updatedAlarmGroup.unificationEnabled
            ? UNIFIED_DATA_VERSION
            : undefined
        }
        handleCamAction={handleAlarmGroupAction}
        onCreateEscalation={handleAlarmGroupAction(CamAction.ESCALATE)}
        onResolveEscalation={handleAlarmGroupAction(
          CamAction.RESOLVE_ESCALATION
        )}
        escalationState={currentAndSiblingEscalations.currentAlarmEscalation}
        onResolveAlarm={navigateToPendingAlarms}
        siblingEscalations={currentAndSiblingEscalations.siblingEscalations}
        onCloseAlarm={handleClose}
        showCloseAlarmButton={true}
      />
    </BlockerPrompt>
  );
}
